import { createContext, FC, ReactNode, useContext, useState } from 'react'
import { useLocation } from 'react-router-dom'

interface ISidebarContext {
  collapsed: boolean
  toggleSidebar: () => void
  setCollapsed: (collapsed: boolean) => void
}

const SidebarContext = createContext<ISidebarContext | undefined>(undefined)

// For components shown both inside the signed-in shell and on guest pages,
// where there is no sidebar at all.
export const useOptionalSidebar = () => useContext(SidebarContext)

export const useSidebar = () => {
  const context = useContext(SidebarContext)
  if (!context) {
    throw new Error('useSidebar must be used within SidebarProvider')
  }
  return context
}

interface ISidebarProvider {
  children: ReactNode
}

const STORAGE_KEY = 'sidebarCollapsed'

const readStored = (): boolean | null => {
  try {
    const value = localStorage.getItem(STORAGE_KEY)
    return value === null ? null : value === 'true'
  } catch {
    return null
  }
}

const persist = (value: boolean) => {
  try {
    localStorage.setItem(STORAGE_KEY, String(value))
  } catch {
    // storage unavailable (private mode) — the choice just won't survive reloads
  }
}

export const SidebarProvider: FC<ISidebarProvider> = ({ children }) => {
  const { pathname } = useLocation()
  // The map and the cabinet live under different layout mounts, so this
  // provider re-mounts when crossing that boundary. Persist the collapsed
  // choice so a manual toggle survives navigation and reloads instead of
  // snapping back (owner feedback J1). Only the very first visit with no
  // stored preference falls back to the map default (collapsed there — 38.3).
  const [collapsed, setCollapsedState] = useState(() => {
    const stored = readStored()
    if (stored !== null) return stored
    return pathname === '/' || pathname.startsWith('/map')
  })

  const setCollapsed = (value: boolean) => {
    setCollapsedState(value)
    persist(value)
  }

  const toggleSidebar = () => {
    setCollapsedState((prev) => {
      const next = !prev
      persist(next)
      return next
    })
  }

  return (
    <SidebarContext.Provider value={{ collapsed, toggleSidebar, setCollapsed }}>
      {children}
    </SidebarContext.Provider>
  )
}
